/**
 * ReseauHeader - Header mobile pour le mode Réseau (Abracadaworld)
 * FORMAT 100% IDENTIQUE AU SHOP (UnifiedHeader)
 * - Bannière Bêta bleue en haut
 * - Header avec Burger + Logo (même taille 32px) + Loupe
 */

import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, X, User, Settings, LogOut, Calendar, Image, 
  MessageSquare, ChevronRight
} from 'lucide-react';
import { useImmersiveScrollContext } from '../../context/ImmersiveScrollContext';

export default function ReseauHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isHidden } = useImmersiveScrollContext();
  const [showHamburger, setShowHamburger] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef(null);
  
  useEffect(() => {
    if (showSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showSearch]);
  
  const hamburgerItems = [
    { id: 'profil', icon: User, label: 'Mon profil', path: '/reseau/profil' },
    { id: 'salons', icon: MessageSquare, label: 'Salons', path: '/reseau/salon' },
    { id: 'evenements', icon: Calendar, label: 'Événements', path: '/reseau/evenements' },
    { id: 'galerie', icon: Image, label: 'Galerie', path: '/reseau/galerie' },
    { id: 'separator', type: 'separator' },
    { id: 'settings', icon: Settings, label: 'Paramètres', path: '/reseau/parametres' },
    { id: 'logout', icon: LogOut, label: 'Déconnexion', action: 'logout' },
  ];
  
  const handleHamburgerItemClick = (item) => {
    if (item.action === 'logout') {
      localStorage.removeItem('ecosystem_token');
      localStorage.removeItem('user_token');
      navigate('/connexion');
    } else if (item.path) {
      navigate(item.path);
    }
    setShowHamburger(false);
  };
  
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/reseau/recherche?q=${encodeURIComponent(searchQuery)}`);
      setShowSearch(false);
      setSearchQuery('');
    }
  };
  
  return (
    <>
      {/* Bandeau bêta supprimé - géré par TopBanner.jsx global */}

      {/* ═══════════════════════════════════════════════════════════════
          HEADER PRINCIPAL - IDENTIQUE AU SHOP
      ═══════════════════════════════════════════════════════════════ */}
      <motion.header
        initial={false}
        animate={{ 
          y: isHidden ? -100 : 0,
          opacity: isHidden ? 0 : 1
        }}
        transition={{ duration: 0.22, ease: 'easeInOut' }}
        className="fixed left-0 right-0 z-50 bg-white md:hidden"
        style={{
          top: '36px', // Sous la bannière bêta
          borderBottom: '1px solid #f0f0f0',
        }}
        data-testid="reseau-header"
      >
        <div 
          className="flex items-center justify-between"
          style={{ 
            height: '52px',
            padding: '0 12px',
          }}
        >
          {/* ═══ GAUCHE : Burger + Logo — IDENTIQUE AU SHOP ═══ */}
          <div className="flex items-center" style={{ gap: '0px' }}>
            {/* Grille 3x3 — MÊME STYLE QUE SHOP */}
            <button
              onClick={() => setShowHamburger(true)}
              className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
              data-testid="reseau-hamburger-btn"
              style={{ 
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" className="text-[#1E3A5F]">
                <rect x="3" y="3" width="5" height="5" rx="1" fill="currentColor"/>
                <rect x="10" y="3" width="5" height="5" rx="1" fill="currentColor"/>
                <rect x="17" y="3" width="5" height="5" rx="1" fill="currentColor"/>
                <rect x="3" y="10" width="5" height="5" rx="1" fill="currentColor"/>
                <rect x="10" y="10" width="5" height="5" rx="1" fill="currentColor"/>
                <rect x="17" y="10" width="5" height="5" rx="1" fill="currentColor"/>
                <rect x="3" y="17" width="5" height="5" rx="1" fill="currentColor"/>
                <rect x="10" y="17" width="5" height="5" rx="1" fill="currentColor"/>
                <rect x="17" y="17" width="5" height="5" rx="1" fill="currentColor"/>
              </svg>
            </button>
            
            {/* Logo AbracadaWorld — MÊME TAILLE QUE ABRACADABATI (32px) */}
            <Link 
              to="/reseau" 
              className="flex items-center" 
              data-testid="reseau-logo"
              style={{ marginLeft: '-2px' }}
            >
              <img 
                src="/images/abracadaworld-logo.png" 
                alt="AbracadaWorld"
                style={{ 
                  height: '26px', 
                  width: 'auto',
                  maxWidth: '160px',
                  objectFit: 'contain',
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              {/* Fallback texte */}
              <div 
                className="items-center hidden"
                style={{ height: '26px' }}
              >
                <span style={{ color: '#1E3A5F', fontWeight: 700, fontSize: '14px' }}>Abracada</span>
                <span style={{ color: '#F97316', fontWeight: 700, fontSize: '14px' }}>World</span>
              </div>
            </Link>
          </div>
          
          {/* ═══ DROITE : Loupe — IDENTIQUE AU SHOP ═══ */}
          <div className="flex items-center" style={{ gap: '8px' }}>
            <button
              onClick={() => setShowSearch(true)}
              className="flex items-center justify-center"
              data-testid="reseau-search-btn"
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                background: '#F97316',
                border: 'none',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              <Search size={18} className="text-white" />
            </button>
          </div>
        </div>
      </motion.header>
      
      {/* Spacer pour la bannière + header */}
      <div className="md:hidden" style={{ height: '88px' }} />
      
      {/* ═══════════════════════════════════════════════════════════════
          OVERLAY RECHERCHE FULLSCREEN
      ═══════════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {showSearch && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 z-[70] bg-white md:hidden"
          >
            <div className="flex items-center gap-3 p-4 border-b border-gray-100">
              <button
                onClick={() => setShowSearch(false)}
                className="p-2 -ml-2 rounded-full hover:bg-gray-100"
              >
                <X size={24} className="text-gray-600" />
              </button>
              
              <form onSubmit={handleSearch} className="flex-1">
                <div className="relative">
                  <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Rechercher un pro, un post, un salon..."
                    className="w-full pl-10 pr-4 py-3 bg-gray-100 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
              </form>
            </div>
            
            <div className="p-4">
              <p className="text-xs text-gray-500 uppercase font-semibold mb-3">Suggestions</p>
              <div className="space-y-2">
                {['Électricien', 'Plombier', 'Rénovation', 'Jardinier', 'Piscine'].map((term) => (
                  <button
                    key={term}
                    onClick={() => {
                      setSearchQuery(term);
                      handleSearch({ preventDefault: () => {} });
                    }}
                    className="flex items-center gap-3 w-full p-3 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <Search size={16} className="text-gray-400" />
                    <span className="text-gray-700">{term}</span>
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* ═══════════════════════════════════════════════════════════════
          MENU HAMBURGER SLIDE-IN
      ═══════════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {showHamburger && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[60] bg-black/50 md:hidden"
              onClick={() => setShowHamburger(false)}
            />
            
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'tween', duration: 0.3 }}
              className="fixed top-0 left-0 bottom-0 z-[70] w-80 bg-white shadow-2xl md:hidden"
            >
              <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-gradient-to-r from-[#1E3A5F] to-[#2d4a6f]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                    <span className="text-white font-bold">A</span>
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">Abracadaworld</p>
                    <p className="text-white/70 text-xs">Réseau Social Pro</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowHamburger(false)}
                  className="p-2 rounded-full hover:bg-white/10"
                >
                  <X size={20} className="text-white" />
                </button>
              </div>
              
              <div className="py-2">
                {hamburgerItems.map((item) => {
                  if (item.type === 'separator') {
                    return <div key={item.id} className="my-2 border-t border-gray-100" />;
                  }
                  
                  const IconComp = item.icon;
                  const isActive = location.pathname === item.path;
                  
                  return (
                    <button
                      key={item.id}
                      onClick={() => handleHamburgerItemClick(item)}
                      className={`flex items-center gap-4 w-full px-4 py-3 hover:bg-gray-50 transition-colors ${
                        isActive ? 'bg-orange-50 border-r-4 border-orange-500' : ''
                      }`}
                    >
                      <IconComp 
                        size={22} 
                        className={isActive ? 'text-orange-500' : 'text-gray-600'} 
                      />
                      <span className={`font-medium ${isActive ? 'text-orange-500' : 'text-gray-700'}`}>
                        {item.label}
                      </span>
                      <ChevronRight size={18} className="ml-auto text-gray-400" />
                    </button>
                  );
                })}
              </div>
              
              <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-100 bg-gray-50">
                <p className="text-xs text-gray-500 text-center">
                  Abracadaworld © 2025
                </p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
      
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </>
  );
}
